import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  Paper,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Snackbar
} from '@mui/material';
import {
  Add,
  Close,
  Delete,
  DirectionsCar,
  Edit,
  Search
} from '@mui/icons-material';
import apiClient from '../../utils/axios';

// Servicio API para placas autorizadas usando apiClient
const placasAPI = {
    async listar(params = {}) {
        try {
          console.log('Enviando parámetros a la API:', params); // Debug
          const response = await apiClient.get('/api/vehiculos/placas-autorizadas', { params });
          console.log('Respuesta completa del servidor:', response); // Debug
          console.log('Status:', response.status); // Debug
          console.log('Data:', response.data); // Debug
          return response.data;
        } catch (error) {
          console.error('Error completo en listar:', error); // Debug
          console.error('Response status:', error.response?.status); // Debug
          console.error('Response data:', error.response?.data); // Debug
          console.error('Request config:', error.config); // Debug
          throw new Error(error.response?.data?.message || 'Error al cargar placas');
        }
      },

  async crear(data) {
    try {
      const response = await apiClient.post('/api/vehiculos/placas-autorizadas', data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Error al crear placa');
    }
  },

  async obtener(id) {
    try {
      const response = await apiClient.get(`/api/vehiculos/placas-autorizadas/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Error al obtener placa');
    }
  },

  async actualizar(id, data) {
    try {
      const response = await apiClient.put(`/api/vehiculos/placas-autorizadas/${id}`, data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Error al actualizar placa');
    }
  },

  async eliminar(id) {
    try {
      const response = await apiClient.delete(`/api/vehiculos/placas-autorizadas/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Error al eliminar placa');
    }
  },

  async verificar(numeroPlaca) {
    try {
      const response = await apiClient.get(`/api/vehiculos/placas-autorizadas/verificar/${numeroPlaca}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Error al verificar placa');
    }
  },


  async buscar(criterios) {
    try {
      const response = await apiClient.post('/api/vehiculos/placas-autorizadas/buscar', criterios);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Error en la búsqueda');
    }
  }
};
const PlacasAutorizadas = ({ open, onClose }) => {
  const [mode, setMode] = useState('view'); // 'view', 'add', 'edit'
  const [loading, setLoading] = useState(false);
  const [placasAutorizadas, setPlacasAutorizadas] = useState([]);
  const [filtros, setFiltros] = useState({
    search: '',
    activa: '',
    page: 1,
    per_page: 50
  });
  
  // Estados para formularios
  const [nuevaPlaca, setNuevaPlaca] = useState({
    numero_placa: '',
    descripcion: '',
    activa: true
  });
  const [placaEditando, setPlacaEditando] = useState(null);
  
  // Estados para notificaciones
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Cargar placas autorizadas usando apiClient
  const loadPlacasAutorizadas = async () => {
    try {
      setLoading(true);
      const response = await placasAPI.listar(filtros);
      if (response.success) {
        console.log(response)
        setPlacasAutorizadas(response.data);
      } else {
        throw new Error(response.message || 'Error cargando placas');
      }
    } catch (error) {
      console.error('Error cargando placas autorizadas:', error);
      console.log(error)
      showSnackbar('Error cargando placas autorizadas', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Mostrar notificación
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Agregar nueva placa usando apiClient
  const handleAgregarPlaca = async () => {
    if (!nuevaPlaca.numero_placa.trim()) {
      showSnackbar('El número de placa es obligatorio', 'error');
      return;
    }
    
    try {
      setLoading(true);
      const response = await placasAPI.crear({
        numero_placa: nuevaPlaca.numero_placa.trim(),
        descripcion: nuevaPlaca.descripcion.trim()
      });
      
      if (response.success) {
        showSnackbar(`Placa ${nuevaPlaca.numero_placa} autorizada exitosamente`);
        setNuevaPlaca({ numero_placa: '', descripcion: '', activa: true });
        setMode('view');
        await loadPlacasAutorizadas();
      }
    } catch (error) {
      console.error('Error agregando placa:', error);
      showSnackbar(error.message || 'Error al agregar placa', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Editar placa
  const handleEditarPlaca = (placa) => {
    setPlacaEditando({
      id: placa.id,
      descripcion: placa.descripcion || '',
      activa: placa.activa
    });
    setMode('edit');
  };

  // Guardar edición usando apiClient
  const handleGuardarEdicion = async () => {
    if (!placaEditando) return;
    
    try {
      setLoading(true);
      const response = await placasAPI.actualizar(placaEditando.id, {
        descripcion: placaEditando.descripcion.trim(),
        activa: placaEditando.activa
      });
      
      if (response.success) {
        showSnackbar('Placa actualizada exitosamente');
        setPlacaEditando(null);
        setMode('view');
        await loadPlacasAutorizadas();
      }
    } catch (error) {
      console.error('Error actualizando placa:', error);
      showSnackbar(error.message || 'Error al actualizar placa', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Activar/desactivar placa usando apiClient
  const handleTogglePlacaStatus = async (placa) => {
    try {
      setLoading(true);
      const response = await placasAPI.actualizar(placa.id, {
        descripcion: placa.descripcion,
        activa: !placa.activa
      });
      
      if (response.success) {
        const estado = !placa.activa ? 'activada' : 'desactivada';
        showSnackbar(`Placa ${placa.numero_placa} ${estado} exitosamente`);
        await loadPlacasAutorizadas();
      }
    } catch (error) {
      console.error('Error actualizando estado de placa:', error);
      showSnackbar(error.message || 'Error al actualizar estado', 'error');
    } finally {
      setLoading(false);
    }
  };

// Reemplaza la función handleEliminarPlaca en tu código:
const handleEliminarPlaca = async (placa) => {
    if (!window.confirm(`¿Estás seguro de eliminar la placa ${placa.numero_placa}?`)) return;
    
    try {
      setLoading(true);
      console.log('Intentando eliminar placa:', placa.id); // Debug
      const response = await placasAPI.eliminar(placa.id);
      console.log('Respuesta del servidor:', response); // Debug
      
      if (response.success) {
        showSnackbar(`Placa ${placa.numero_placa} eliminada exitosamente`);
        await loadPlacasAutorizadas();
      }
    } catch (error) {
      console.error('Error completo eliminando placa:', error); // Más detalle
      console.error('Response data:', error.response?.data); // Datos del servidor
      console.error('Status:', error.response?.status); // Código de estado
      showSnackbar(error.message || 'Error al eliminar placa', 'error');
    } finally {
      setLoading(false);
    }
  };
  

  // Filtrar placas usando apiClient
  const handleFiltrarPlacas = async () => {
    await loadPlacasAutorizadas();
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleFiltrarPlacas();
    }
  };

  // Cerrar diálogo
  const handleClose = () => {
    setMode('view');
    setNuevaPlaca({ numero_placa: '', descripcion: '', activa: true });
    setPlacaEditando(null);
    setFiltros({ search: '', activa: '', page: 1, per_page: 50 });
    onClose();
  };

  // Cargar datos al abrir usando apiClient
  useEffect(() => {
    if (open) {
      loadPlacasAutorizadas();
    }
  }, [open]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          background: 'linear-gradient(135deg, #393939 0%, #4a4a4a 100%)',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DirectionsCar />
            {mode === 'add' && 'Agregar Placa Autorizada'}
            {mode === 'edit' && 'Editar Placa Autorizada'}
            {mode === 'view' && 'Placas Autorizadas'}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton 
              onClick={handleClose}
              size="small"
              sx={{ color: 'white' }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ py: 3 }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <CircularProgress />
            </Box>
          )}

          {mode === 'add' && (
            <Box>
              <TextField
                fullWidth
                label="Número de Placa *"
                value={nuevaPlaca.numero_placa}
                onChange={(e) => setNuevaPlaca(prev => ({ 
                  ...prev, 
                  numero_placa: e.target.value.toUpperCase() 
                }))}
                margin="normal"
                placeholder="Ej: ABC123"
                inputProps={{ maxLength: 20 }}
                error={!nuevaPlaca.numero_placa.trim()}
                helperText={!nuevaPlaca.numero_placa.trim() ? 'Campo obligatorio' : ''}
              />
              
              <TextField
                fullWidth
                label="Descripción"
                value={nuevaPlaca.descripcion}
                onChange={(e) => setNuevaPlaca(prev => ({ 
                  ...prev, 
                  descripcion: e.target.value 
                }))}
                margin="normal"
                multiline
                rows={2}
                placeholder="Ej: Vehículo administrativo, Vehículo de emergencia, etc."
              />
            </Box>
          )}

          {mode === 'edit' && placaEditando && (
            <Box>
              <TextField
                fullWidth
                label="Descripción"
                value={placaEditando.descripcion}
                onChange={(e) => setPlacaEditando(prev => ({ 
                  ...prev, 
                  descripcion: e.target.value 
                }))}
                margin="normal"
                multiline
                rows={2}
              />
              
              <FormControl margin="normal">
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Estado:</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label="Activa"
                    color={placaEditando.activa ? "success" : "default"}
                    onClick={() => setPlacaEditando(prev => ({ ...prev, activa: true }))}
                    variant={placaEditando.activa ? "filled" : "outlined"}
                    clickable
                  />
                  <Chip
                    label="Inactiva"
                    color={!placaEditando.activa ? "error" : "default"}
                    onClick={() => setPlacaEditando(prev => ({ ...prev, activa: false }))}
                    variant={!placaEditando.activa ? "filled" : "outlined"}
                    clickable
                  />
                </Box>
              </FormControl>
            </Box>
          )}

          {mode === 'view' && (
            <Box sx={{m: 2}}>
              {/* Filtros */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                size="small"
                label="Buscar placa"
                value={filtros.search}
                onChange={(e) => setFiltros(prev => ({ ...prev, search: e.target.value }))}
                onKeyPress={handleKeyPress}  // ← AGREGAR ESTA LÍNEA
                placeholder="Buscar por placa o descripción"
                InputProps={{
                  startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />
                }}
                sx={{ minWidth: 200 }}
              />
                
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Typography variant="caption" sx={{ mb: 0.5 }}>Estado:</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip
                      label="Todas"
                      color={filtros.activa === '' ? "primary" : "default"}
                      onClick={() => setFiltros(prev => ({ ...prev, activa: '' }))}
                      variant={filtros.activa === '' ? "filled" : "outlined"}
                      size="small"
                      clickable
                    />
                    <Chip
                      label="Activas"
                      color={filtros.activa === 'true' ? "success" : "default"}
                      onClick={() => setFiltros(prev => ({ ...prev, activa: 'true' }))}
                      variant={filtros.activa === 'true' ? "filled" : "outlined"}
                      size="small"
                      clickable
                    />
                    <Chip
                      label="Inactivas"
                      color={filtros.activa === 'false' ? "error" : "default"}
                      onClick={() => setFiltros(prev => ({ ...prev, activa: 'false' }))}
                      variant={filtros.activa === 'false' ? "filled" : "outlined"}
                      size="small"
                      clickable
                    />
                  </Box>
                </FormControl>

                <Button
                  variant="outlined"
                  onClick={handleFiltrarPlacas}
                  disabled={loading}
                >
                  Filtrar
                </Button>

                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setMode('add')}
                  disabled={loading}
                  sx={{ 
                    background: "#393939ff",
                    textTransform: "none",
                    ml: 'auto'
                  }}
                >
                  Agregar Placa
                </Button>
              </Box>

              {/* Lista de placas */}
              {placasAutorizadas.length === 0 && !loading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <DirectionsCar sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No hay placas autorizadas
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={3}>
                    {filtros.search || filtros.activa ? 'No se encontraron placas con los filtros aplicados' : 'Agrega la primera placa autorizada al sistema'}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {placasAutorizadas.map((placa) => (
                    <Paper
                      key={placa.id}
                      sx={{
                        p: 2,
                        mb: 2,
                        border: '1px solid #e0e0e0',
                        borderLeft: `4px solid ${placa.activa ? '#4caf50' : '#f44336'}`,
                        transition: 'all 0.2s',
                        '&:hover': { boxShadow: 2 }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
                              {placa.numero_placa}
                            </Typography>
                            <Chip
                              label={placa.activa ? 'Activa' : 'Inactiva'}
                              color={placa.activa ? 'success' : 'error'}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                          
                          <Typography variant="body2" color="text.secondary">
                            {placa.descripcion || 'Sin descripción'}
                          </Typography>
                          
                          {placa.fecha_creacion && (
                            <Typography variant="caption" color="text.secondary">
                              Creada: {new Date(placa.fecha_creacion).toLocaleDateString()}
                            </Typography>
                          )}
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditarPlaca(placa)}
                            color="primary"
                            title="Editar"
                            disabled={loading}
                          >
                            <Edit />
                          </IconButton>
                                                  
                          <IconButton
                            size="small"
                            onClick={() => handleEliminarPlaca(placa)}
                            color="error"
                            title="Eliminar"
                            disabled={loading}
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3 }}>
          {mode === 'add' && (
            <>
              <Button 
                onClick={() => setMode('view')}
                color="inherit"
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleAgregarPlaca}
                variant="contained"
                disabled={!nuevaPlaca.numero_placa.trim() || loading}
                sx={{ 
                  background: "#393939ff",
                  "&:hover": { background: "#2a2a2a" }
                }}
              >
                Agregar Placa
              </Button>
            </>
          )}

          {mode === 'edit' && (
            <>
              <Button 
                onClick={() => {
                  setMode('view');
                  setPlacaEditando(null);
                }}
                color="inherit"
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleGuardarEdicion}
                variant="contained"
                disabled={loading}
                sx={{ 
                  background: "#393939ff",
                  "&:hover": { background: "#2a2a2a" }
                }}
              >
                Guardar Cambios
              </Button>
            </>
          )}

          {mode === 'view' && (
            <Button 
              onClick={handleClose}
              variant="contained"
              sx={{ 
                background: "#393939ff",
                "&:hover": { background: "#2a2a2a" }
              }}
            >
              Cerrar
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PlacasAutorizadas;